import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  CalendarDaysIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import { availabilityService } from '../services/availabilityService'
import { TimeOffPeriod } from '../types/availability'

interface TimeOffManagerProps {
  isOpen: boolean
  onClose: () => void
  selectedDate?: string // Pre-fill with selected date if provided
  onTimeOffCreated?: () => void // Callback when time off is created
}

const TimeOffManager = ({ isOpen, onClose, selectedDate, onTimeOffCreated }: TimeOffManagerProps) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [timeOffPeriods, setTimeOffPeriods] = useState<TimeOffPeriod[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: selectedDate || '',
    endDate: selectedDate || '',
    startTime: '',
    endTime: '',
    type: 'personal' as TimeOffPeriod['type'],
    reason: '',
    isRecurring: false
  })

  const timeOffTypes = [
    { value: 'vacation', label: 'Vacation', color: 'bg-blue-500' },
    { value: 'sick', label: 'Sick Leave', color: 'bg-red-500' },
    { value: 'personal', label: 'Personal Time', color: 'bg-green-500' },
    { value: 'holiday', label: 'Holiday', color: 'bg-purple-500' },
    { value: 'travel', label: 'Travel', color: 'bg-yellow-500' },
    { value: 'family', label: 'Family Time', color: 'bg-pink-500' },
    { value: 'other', label: 'Other', color: 'bg-gray-500' }
  ]

  useEffect(() => {
    if (isOpen && user?.id) {
      loadTimeOffPeriods()
    }
  }, [isOpen, user?.id])

  useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        startDate: selectedDate,
        endDate: selectedDate
      }))
    }
  }, [selectedDate])

  const loadTimeOffPeriods = async () => {
    try {
      setLoading(true)
      const periods = await availabilityService.getTimeOffPeriods(user!.id)
      setTimeOffPeriods(periods)
    } catch (error) {
      console.error('Error loading time off periods:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.id) return

    try {
      setSaving(true)

      const timeOffData: Omit<TimeOffPeriod, 'id' | 'createdAt' | 'updatedAt'> = {
        userId: user.id,
        title: formData.title,
        description: formData.description || undefined,
        startDate: formData.startDate,
        endDate: formData.endDate,
        startTime: formData.startTime || undefined,
        endTime: formData.endTime || undefined,
        type: formData.type,
        reason: formData.reason || undefined,
        status: 'approved',
        isRecurring: formData.isRecurring
      }

      if (editingId) {
        await availabilityService.updateTimeOffPeriod(editingId, timeOffData)
      } else {
        await availabilityService.createTimeOffPeriod(timeOffData)
      }

      await loadTimeOffPeriods()
      resetForm()
      onTimeOffCreated?.()
    } catch (error) {
      console.error('Error saving time off period:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (period: TimeOffPeriod) => {
    setFormData({
      title: period.title,
      description: period.description || '',
      startDate: period.startDate,
      endDate: period.endDate,
      startTime: period.startTime || '',
      endTime: period.endTime || '',
      type: period.type,
      reason: period.reason || '',
      isRecurring: period.isRecurring
    })
    setEditingId(period.id)
    setShowAddForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this time off period?')) return

    try {
      await availabilityService.deleteTimeOffPeriod(id)
      await loadTimeOffPeriods()
      onTimeOffCreated?.()
    } catch (error) {
      console.error('Error deleting time off period:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      startDate: selectedDate || '',
      endDate: selectedDate || '',
      startTime: '',
      endTime: '',
      type: 'personal',
      reason: '',
      isRecurring: false
    })
    setEditingId(null)
    setShowAddForm(false)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (time: string) => {
    if (!time) return null
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const getTypeInfo = (type: TimeOffPeriod['type']) => {
    return timeOffTypes.find(t => t.value === type) || timeOffTypes[0]
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-4xl max-h-[90vh] glass-mockup rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center">
                <CalendarDaysIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Time Off Management</h2>
                <p className="text-gray-400 text-sm">Schedule and manage your time off periods</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Add New Time Off Button */}
            {!showAddForm && (
              <motion.button
                onClick={() => setShowAddForm(true)}
                className="w-full mb-6 p-4 glass-mockup rounded-xl border border-white/10 hover:border-white/20 transition-colors group"
              >
                <div className="flex items-center justify-center space-x-3 text-gray-400 group-hover:text-white transition-colors">
                  <PlusIcon className="w-5 h-5" />
                  <span className="font-medium">Schedule New Time Off</span>
                </div>
              </motion.button>
            )}

            {/* Add/Edit Form */}
            {showAddForm && (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleSubmit}
                className="mb-6 glass-mockup rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4">
                  {editingId ? 'Edit Time Off' : 'Schedule New Time Off'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Title */}
                  <div className="md:col-span-2">
                    <label className="block text-white text-sm font-medium mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="input-field"
                      placeholder="e.g., Vacation, Doctor's appointment"
                      required
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as TimeOffPeriod['type'] }))}
                      className="input-field"
                    >
                      {timeOffTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Reason (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      className="input-field"
                      placeholder="Brief reason or note"
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="input-field"
                      required
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                      className="input-field"
                      min={formData.startDate}
                      required
                    />
                  </div>

                  {/* Start Time (Optional) */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Start Time (Optional)
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                      className="input-field"
                    />
                    <p className="text-xs text-gray-400 mt-1">Leave empty for full day</p>
                  </div>

                  {/* End Time (Optional) */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      End Time (Optional)
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                      className="input-field"
                      disabled={!formData.startTime}
                      min={formData.startTime}
                    />
                    <p className="text-xs text-gray-400 mt-1">Required if start time is set</p>
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-white text-sm font-medium mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="input-field resize-none h-20"
                      placeholder="Additional details about your time off"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !formData.title || !formData.startDate || !formData.endDate}
                    className="px-6 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:from-red-600 hover:to-pink-600 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {saving && (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )}
                    <span>{editingId ? 'Update' : 'Schedule'} Time Off</span>
                  </button>
                </div>
              </motion.form>
            )}

            {/* Time Off List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Scheduled Time Off</h3>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              ) : timeOffPeriods.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarDaysIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">No time off scheduled</p>
                  <p className="text-sm text-gray-500">Schedule your vacation days, sick leave, and personal time</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {timeOffPeriods.map((period) => {
                    const typeInfo = getTypeInfo(period.type)
                    const isMultiDay = period.startDate !== period.endDate
                    const isPartialDay = period.startTime && period.endTime

                    return (
                      <motion.div
                        key={period.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="glass-mockup rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className={`w-3 h-3 ${typeInfo.color} rounded-full mt-2 flex-shrink-0`} />
                            <div className="flex-1">
                              <h4 className="font-semibold text-white">{period.title}</h4>
                              <div className="flex items-center space-x-2 text-sm text-gray-300 mt-1">
                                <span className={`px-2 py-1 ${typeInfo.color} bg-opacity-20 text-white rounded text-xs`}>
                                  {typeInfo.label}
                                </span>
                                <span>•</span>
                                <span>
                                  {isMultiDay ? (
                                    `${formatDate(period.startDate)} - ${formatDate(period.endDate)}`
                                  ) : (
                                    formatDate(period.startDate)
                                  )}
                                </span>
                                {isPartialDay && (
                                  <>
                                    <span>•</span>
                                    <span>{formatTime(period.startTime!)} - {formatTime(period.endTime!)}</span>
                                  </>
                                )}
                              </div>
                              {period.reason && (
                                <p className="text-sm text-gray-400 mt-1">{period.reason}</p>
                              )}
                              {period.description && (
                                <p className="text-sm text-gray-400 mt-2">{period.description}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEdit(period)}
                              className="p-2 text-gray-400 hover:text-blue-400 transition-colors rounded-lg hover:bg-white/10"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(period.id)}
                              className="p-2 text-gray-400 hover:text-red-400 transition-colors rounded-lg hover:bg-white/10"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default TimeOffManager 